import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { CreditProvider } from '@/contexts/CreditContext';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { DashboardLayout, AuthLayout } from '@/components/layout';
import {
  VerdictLandingPage,
  SignupPage,
  LoginPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmail,
  ThankYouPage,
  DashboardPage,
  VerdictDashboardPage,
  StockAnalysisPage,
  StrategicAnalysisPage,
  PortfolioPage,
  PreferencesPage,
  PlansPage,
  PricingPage,
  HistoryPage,
  FeedbackPage,
  CheckoutPage,
  CheckoutSuccessPage,
  CheckoutCancelPage,
  CreditPurchaseSuccessPage,
  CreditPurchaseCancelPage,
  PurchaseHistoryPage,
  PrivacyPolicyPage,
  TermsOfServicePage,
  NotFoundPage,
  AdminMonitoringPage,
  VerdictAdminMonitoringPage,
  ResearchHistoryPage,
  BenchmarkPage,
  DocsPage,
} from '@/pages';
import { LoadingSpinner } from '@/components/ui';

// Component to handle /plans route - shows public pricing or dashboard plans based on auth
function PlansRouter() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // If not authenticated, show public pricing page
  if (!isAuthenticated) {
    return <PricingPage />;
  }

  // If authenticated, redirect to dashboard plans (handled by protected route below)
  return <Navigate to="/dashboard/plans" replace />;
}

function App() {
  return (
    <ThemeProvider>
    <HelmetProvider>
    <BrowserRouter>
      <AuthProvider>
        <CreditProvider>
        <Routes>
          {/* Public Routes */}
          {/* VERDICT is now the main landing page */}
          <Route path="/" element={<VerdictLandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/benchmark" element={<BenchmarkPage />} />
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/plans" element={<PlansRouter />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />

          {/* Checkout Routes */}
          <Route
            path="/checkout"
            element={
              <ProtectedRoute>
                <CheckoutPage />
              </ProtectedRoute>
            }
          />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />

          {/* Credit Purchase Routes */}
          <Route path="/credits/success" element={<CreditPurchaseSuccessPage />} />
          <Route path="/credits/cancel" element={<CreditPurchaseCancelPage />} />

          {/* Purchase History (Protected) */}
          <Route
            path="/purchases"
            element={
              <ProtectedRoute>
                <PurchaseHistoryPage />
              </ProtectedRoute>
            }
          />

          {/* Auth Routes */}
          <Route element={<AuthLayout />}>
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </Route>

          {/* Email Verification Route (Public) */}
          <Route path="/verify-email" element={<VerifyEmail />} />

          {/* Legacy: /customize now redirects to dashboard - newsletter setup is via widget */}
          <Route
            path="/customize"
            element={
              <ProtectedRoute>
                <Navigate to="/dashboard" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/thank-you"
            element={
              <ProtectedRoute>
                <ThankYouPage />
              </ProtectedRoute>
            }
          />

          {/* Verdict Dashboard - Chat-like research interface (Public access allowed for free trial) */}
          <Route path="/dashboard" element={<VerdictDashboardPage />} />

          {/* Stock Analysis & Strategic - Own sidebar layout (like VerdictDashboardPage) */}
          <Route path="/dashboard/stock-analysis" element={<ProtectedRoute><StockAnalysisPage /></ProtectedRoute>} />
          <Route path="/dashboard/strategic" element={<ProtectedRoute><StrategicAnalysisPage /></ProtectedRoute>} />

          {/* Dashboard Routes (Protected with sidebar layout) */}
          <Route
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            {/* Primary sections */}
            <Route path="/dashboard/portfolio" element={<PortfolioPage />} />
            {/* Legacy & utility */}
            <Route path="/dashboard/legacy" element={<DashboardPage />} />
            <Route path="/dashboard/plans" element={<PlansPage />} />
            <Route path="/profile" element={<Navigate to="/preferences?tab=profile" replace />} />
            <Route path="/preferences" element={<PreferencesPage />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/research" element={<ResearchHistoryPage />} />
            <Route path="/feedback" element={<FeedbackPage />} />
            {/* Admin-only routes - components handle access check */}
            <Route path="/admin/monitoring" element={<AdminMonitoringPage />} />
            <Route path="/admin/verdict" element={<VerdictAdminMonitoringPage />} />
          </Route>

          {/* 404 Page - helps Google understand dead links */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </CreditProvider>
      </AuthProvider>
    </BrowserRouter>
    </HelmetProvider>
    </ThemeProvider>
  );
}

export default App;

